import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { listDocuments, deleteDocument } from "@/lib/api";
import { toast } from "sonner";
import { useAuth, ROLES } from "@/contexts/AuthContext";
import {
  FileText,
  Trash2,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  ArrowRight,
  Plus,
} from "lucide-react";

const statusPill = {
  done: { label: "Completata", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  analyzing: { label: "In analisi", cls: "bg-blue-100 text-blue-700", icon: Loader2 },
  pending: { label: "In coda", cls: "bg-slate-100 text-slate-600", icon: Clock },
  error: { label: "Errore", cls: "bg-red-100 text-red-700", icon: AlertCircle },
};

const History = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { caps, effectiveRole } = useAuth();

  const load = useCallback(async () => {
    try {
      const items = await listDocuments();
      setDocs(items);
    } catch {
      toast.error("Impossibile caricare i documenti");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id) => {
    try {
      await deleteDocument(id);
      setDocs((ds) => ds.filter((d) => d.id !== id));
      toast.success("Documento rimosso");
    } catch {
      toast.error("Errore durante l'eliminazione");
    }
  };

  return (
    <div
      data-testid="history-page"
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14"
    >
      <div className="flex flex-wrap items-end justify-between gap-4 animate-fade-up">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">
            Storico
          </span>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-black tracking-tight">
            Contratti
          </h1>
          <p className="mt-2 text-slate-600">
            {caps?.canUpload
              ? "Tutti i documenti analizzati dal team."
              : `Vista ${ROLES[effectiveRole]?.label}. Apri un contratto per vedere ciò che ti riguarda.`}
          </p>
        </div>
        {caps?.canUpload && (
          <Link
            to="/"
            data-testid="new-analysis-btn"
            className="inline-flex items-center gap-2 bg-slate-900 text-white font-semibold px-5 py-2.5 rounded-md hover:bg-slate-800 transition-colors"
          >
            <Plus size={16} />
            Nuova analisi
          </Link>
        )}
      </div>

      <div className="mt-10">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : docs.length === 0 ? (
          <div
            data-testid="history-empty"
            className="text-center py-20 border-2 border-dashed border-slate-200 rounded-2xl bg-white"
          >
            <div className="w-16 h-16 mx-auto rounded-2xl bg-slate-100 flex items-center justify-center">
              <FileText className="w-7 h-7 text-slate-400" />
            </div>
            <h3 className="mt-5 font-display text-xl font-bold">Nessun contratto ancora</h3>
            <p className="mt-2 text-slate-500 text-sm">
              {caps?.canUpload
                ? "Carica il tuo primo contratto per iniziare."
                : "Il team non ha ancora caricato contratti."}
            </p>
            {caps?.canUpload && (
              <Link
                to="/"
                className="mt-5 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-2.5 rounded-md transition-colors"
              >
                <Plus size={16} />
                Carica un PDF
              </Link>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {docs.map((d, i) => {
              const s = statusPill[d.status] || statusPill.pending;
              const Icon = s.icon;
              const a = d.analysis || {};
              const highRisks = (a.rischi || []).filter((r) => r.severita === "HIGH").length;
              const docCount = (a.documenti_richiesti?.checklist || []).length;
              return (
                <div
                  key={d.id}
                  data-testid={`history-card-${i}`}
                  className="group relative p-5 bg-white border border-slate-200 rounded-xl hover:border-slate-900 hover:-translate-y-0.5 transition-all"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                      <FileText size={16} />
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${s.cls}`}
                    >
                      <Icon size={10} className={d.status === "analyzing" ? "animate-spin" : ""} />
                      {s.label}
                    </span>
                  </div>

                  <h3 className="mt-4 font-display font-bold text-base truncate" title={d.filename}>
                    {d.filename}
                  </h3>
                  <p className="mt-1 text-xs text-slate-500 font-mono">
                    {d.pages} pagine · {(d.text_length / 1000).toFixed(1)}k car.
                  </p>

                  {d.status === "done" && (
                    <div className="mt-4 flex items-center gap-3 text-xs">
                      {highRisks > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold">
                          {highRisks} HIGH
                        </span>
                      )}
                      {docCount > 0 && (
                        <span className="text-slate-600">{docCount} documenti</span>
                      )}
                      {a.sommario?.valore_contratto && (
                        <span className="font-mono text-slate-600 truncate">
                          {a.sommario.valore_contratto}
                        </span>
                      )}
                    </div>
                  )}
                  {d.status === "error" && d.error && (
                    <p className="mt-3 text-xs text-red-700 line-clamp-2">{d.error}</p>
                  )}

                  <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
                    <Link
                      to={`/analisi/${d.id}`}
                      data-testid={`open-doc-${i}`}
                      className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1 group-hover:gap-2 transition-all"
                    >
                      Apri
                      <ArrowRight size={14} />
                    </Link>
                    {caps?.canDelete && (
                      <button
                        onClick={() => handleDelete(d.id)}
                        data-testid={`delete-doc-${i}`}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default History;
